import { LifebuoyIcon } from "@phosphor-icons/react/dist/csr/Lifebuoy";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { LogoIcon } from "@volca/ui/components/ui/logo";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { openSupportChat, useHasPylonAccess } from "@/core/pylon";
import { usePylonChatAnchor } from "@/core/pylon/use-pylon-chat-anchor";
import { useCommandMenu } from "./command-menu";
import { useNavColumn } from "./nav-column-state";
import type { RailEntry } from "./rail-model";
import { SidebarNavSkeleton } from "./sidebar-nav-skeleton";
import { ColumnReveal, RailControl } from "./sidebar-rail-control";

const SupportButton = () => {
  const hasAccess = useHasPylonAccess();
  const ref = usePylonChatAnchor(hasAccess, "right", false);
  if (!hasAccess) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            ref={ref}
            type="button"
            aria-label="Contact support"
            className="grid size-8 place-items-center rounded-lg text-ink outline-hidden transition-[background-color,color] duration-150 ease-out hover:bg-neutral-subtle-hovered focus-visible:ring-2 focus-visible:ring-focus motion-safe:active:translate-y-px"
            onClick={openSupportChat}
          />
        }
      >
        <LifebuoyIcon className="size-4" />
      </TooltipTrigger>
      <TooltipContent side="right">Contact support</TooltipContent>
    </Tooltip>
  );
};

export const SidebarIconRail = ({ account }: { account?: ReactNode }) => {
  const { setOpen, opensOnHelp } = useCommandMenu();
  const column = useNavColumn();
  const searchLabel = opensOnHelp ? "Search help" : "Search";

  const rail = (entries: RailEntry[], gap: 0.5 | 1 = 1) => (
    <Stack gap={gap} align="center" className="w-full">
      {entries.map((entry) => (
        <RailControl key={entry.id} entry={entry} />
      ))}
    </Stack>
  );

  return (
    <Stack
      render={<aside aria-label="Sidebar" />}
      align="center"
      gap={2}
      className="min-h-0 w-12 shrink-0 overflow-x-hidden px-2 py-2"
    >
      <Link
        to="/"
        aria-label="Volca home"
        className="grid size-8 place-items-center rounded-lg outline-hidden focus-visible:ring-2 focus-visible:ring-focus"
      >
        <LogoIcon width={22} height={18} />
      </Link>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="outline"
              size="icon"
              aria-label={searchLabel}
              aria-keyshortcuts="Meta+K Control+K"
              onClick={() => setOpen(true)}
              className="transition-[background-color,color,border-color] duration-150 ease-[var(--sidebar-ease-out)]"
            />
          }
        >
          <MagnifyingGlassIcon />
        </TooltipTrigger>
        <TooltipContent side="right">{searchLabel}</TooltipContent>
      </Tooltip>
      <Stack
        gap={0}
        className="min-h-0 w-full flex-1 overflow-x-hidden overflow-y-auto overscroll-contain"
      >
        {column.active && (
          <ColumnReveal
            label={column.active.label}
            open={column.hidden}
            onShow={column.show}
          />
        )}
        {column.isLoading ? (
          <SidebarNavSkeleton />
        ) : (
          <nav aria-label="Main" className="w-full">
            <Stack gap={2} align="center" className="w-full">
              {column.productGroups.map((group, index) => (
                <Stack key={group.id} gap={2} align="center" className="w-full">
                  {index > 0 && (
                    <span aria-hidden className="h-px w-4 shrink-0 bg-line" />
                  )}
                  {rail(group.entries)}
                </Stack>
              ))}
            </Stack>
          </nav>
        )}
      </Stack>
      <span aria-hidden className="-mx-2 h-px w-12 shrink-0 bg-line" />
      <Stack gap={0.5} align="center" className="w-full">
        <nav aria-label="Workspace" className="w-full">
          {rail(column.utility, 0.5)}
        </nav>
        <SupportButton />
        {account}
      </Stack>
    </Stack>
  );
};
